// ©2024 Austin App House. All rights reserved.
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import Modal from '../../components/modal';
import Notification from '../../components/notification';
import AdminContractorSearch from '../../components/adminContractors/AdminContractorSearch';
import AdminContractorFilters from '../../components/adminContractors/AdminContractorFilters';
import RdtPagination from '../../components/datatables/RdtPagination';
import Loader from '../../components/dashboard/common/Loader';
import { contractorColumnsForSuperAdmin } from '../../utils/constans';
import { orderCertifications } from '../../utils/certificatesOrdering';
import {
  fetchAdminContractors,
  getContractorUserId,
  updateContractorSuspension,
  updateContractorDeactivation,
  isContractorDeactivated,
} from '../../utils/api/adminContractors';
import { toggleModal } from '../../storage/actions/modalsActions';
import { reloadData } from '../../storage/actions/applicationStateActions';
import DashboardIcon from '../../components/dashboard/icons/DashboardIcon';

const PER_PAGE_OPTIONS = [15, 30, 50];

const renderStatusPill = (row) => {
  if (isContractorDeactivated(row)) {
    return <span className="status-pill status-pill--muted">Deactivated</span>;
  }

  if (row.suspended) {
    return <span className="status-pill status-pill--danger">Suspended</span>;
  }

  const status = String(row.status || 'active').toLowerCase();

  if (status === 'verified' || status === 'active') {
    return <span className="status-pill status-pill--accepted">{status}</span>;
  }

  if (status === 'unverified') {
    return <span className="status-pill status-pill--pending">Unverified</span>;
  }

  return <span className="status-pill status-pill--pending">{status}</span>;
};

const Contractors = ({
  toggleModal,
  reloadData: reloadAppData,
  mainContainer,
  isReload,
}) => {
  const [contractors, setContractors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [certificates, setCertificates] = useState([]);
  const [selectedCertificates, setSelectedCertificates] = useState(new Set());
  const [suspendedFilter, setSuspendedFilter] = useState('all');
  const [registeredDateRange, setRegisteredDateRange] = useState({ from: null, to: null });
  const [lastLoginRange, setLastLoginRange] = useState({ from: null, to: null });
  const [actionLoadingId, setActionLoadingId] = useState(null);

  useEffect(() => {
    axios.get('/api/certificates')
      .then((res) => {
        setCertificates(orderCertifications(res.data.certificates || []));
      })
      .catch((error) => {
        Notification('error', {
          message: error.response?.data?.message || error.message,
        });
      });
  }, []);

  const loadContractors = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchAdminContractors({
        page,
        perPage,
        sortBy,
        sortDirection,
        search: searchFilters.search || '',
        zipcode: searchFilters.search_zip || '',
        range: searchFilters.range,
        certificates: [...selectedCertificates],
        suspended: suspendedFilter,
        registeredDateRange,
        lastLoginRange,
      });
      setContractors(result.payload);
      setTotal(result.total);
    } catch (error) {
      setContractors([]);
      setTotal(0);
      Notification('error', {
        message: error.response?.data?.message || error.message,
      });
    } finally {
      setLoading(false);
    }
  }, [
    page,
    perPage,
    sortBy,
    sortDirection,
    searchFilters,
    selectedCertificates,
    suspendedFilter,
    registeredDateRange,
    lastLoginRange,
  ]);

  useEffect(() => {
    loadContractors();
  }, [loadContractors, isReload]);

  const handleSearch = (values) => {
    setPage(1);
    setSearchFilters(values);
  };

  const handleCertificateChange = ({ target }) => {
    setPage(1);
    setSelectedCertificates((prev) => {
      const next = new Set(prev);
      if (target.checked) next.add(target.value);
      else next.delete(target.value);
      return next;
    });
  };

  const handleSuspendedFilterChange = (value) => {
    setPage(1);
    setSuspendedFilter(value);
  };

  const handleRegisteredDateRangeChange = (range) => {
    setPage(1);
    setRegisteredDateRange(range);
  };

  const handleLastLoginRangeChange = (range) => {
    setPage(1);
    setLastLoginRange(range);
  };

  const runContractorAction = async (contractorId, action, successMessage) => {
    setActionLoadingId(contractorId);
    try {
      const result = await action();
      Notification('success', { message: result?.message || successMessage });
      await loadContractors();
      reloadAppData();
    } catch (error) {
      Notification('error', {
        message: error.response?.data?.message || error.message,
      });
      throw error;
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleSuspend = (row) => {
    const contractorId = getContractorUserId(row);
    toggleModal(true, 'suspendContractorModal', false, {
      contractorName: row.name || 'this contractor',
      onConfirm: ({ reason, timeframe }) => runContractorAction(
        contractorId,
        () => updateContractorSuspension(contractorId, true, { reason, timeframe }),
        `${row.name || 'Contractor'} suspended successfully.`,
      ),
    });
  };

  const handleUnsuspend = (row) => {
    const contractorId = getContractorUserId(row);
    toggleModal(true, 'confirmModal', false, {
      header: 'Unsuspend account',
      content: `Unsuspend ${row.name || 'this contractor'}? They will be able to apply to and accept work again, and will be emailed that the suspension has been lifted.`,
      buttonLabels: { confirm: 'Unsuspend', reject: 'Cancel' },
      onAccept: () => runContractorAction(
        contractorId,
        () => updateContractorSuspension(contractorId, false),
        `${row.name || 'Contractor'} unsuspended successfully.`,
      ),
      onReject: () => {},
    });
  };

  const handleDeactivate = (row) => {
    const contractorId = getContractorUserId(row);
    toggleModal(true, 'deactivateContractorModal', false, {
      contractorName: row.name || 'this contractor',
      onConfirm: ({ reason }) => runContractorAction(
        contractorId,
        () => updateContractorDeactivation(contractorId, true, { reason }),
        `${row.name || 'Contractor'} deactivated successfully.`,
      ),
    });
  };

  const handleReactivate = (row) => {
    const contractorId = getContractorUserId(row);
    toggleModal(true, 'confirmModal', false, {
      header: 'Reactivate contractor',
      content: `Reactivate ${row.name || 'this contractor'}? They will return to active status and regain full access.`,
      buttonLabels: { confirm: 'Reactivate', reject: 'Cancel' },
      onAccept: () => runContractorAction(
        contractorId,
        () => updateContractorDeactivation(contractorId, false),
        `${row.name || 'Contractor'} reactivated successfully.`,
      ),
      onReject: () => {},
    });
  };

  const columns = useMemo(() => [
    ...contractorColumnsForSuperAdmin.map((column) => (
      column.name === 'Status'
        ? {
          ...column,
          cell: (row) => renderStatusPill(row),
        }
        : column
    )),
    {
      name: 'Actions',
      width: '160px',
      center: true,
      cell: (row) => {
        const contractorId = getContractorUserId(row);
        const isBusy = actionLoadingId === contractorId;
        const deactivated = isContractorDeactivated(row);
        return (
          <div className="row-actions">
            {row.suspended ? (
              <button
                type="button"
                className="row-action-btn row-action-btn--restore"
                data-tooltip="UNSUSPEND ACCOUNT"
                aria-label="Unsuspend account"
                disabled={isBusy || deactivated}
                onClick={() => handleUnsuspend(row)}
              >
                <DashboardIcon name="ban" size={18} strokeWidth="2.2" />
              </button>
            ) : (
              <button
                type="button"
                className="row-action-btn row-action-btn--suspend"
                data-tooltip="SUSPEND ACCOUNT"
                aria-label="Suspend account"
                disabled={isBusy || deactivated}
                onClick={() => handleSuspend(row)}
              >
                <DashboardIcon name="ban" size={18} strokeWidth="2.2" />
              </button>
            )}

            {deactivated ? (
              <button
                type="button"
                className="row-action-btn row-action-btn--reactivate"
                data-tooltip="REACTIVATE CONTRACTOR"
                aria-label="Reactivate contractor"
                disabled={isBusy}
                onClick={() => handleReactivate(row)}
              >
                <DashboardIcon name="confirmedCheck" size={18} strokeWidth="2.4" />
              </button>
            ) : (
              <button
                type="button"
                className="row-action-btn row-action-btn--deactivate"
                data-tooltip="DEACTIVATE CONTRACTOR"
                aria-label="Deactivate contractor"
                disabled={isBusy}
                onClick={() => handleDeactivate(row)}
              >
                <DashboardIcon name="close" size={18} strokeWidth="2.4" />
              </button>
            )}
          </div>
        );
      },
    },
  ], [actionLoadingId, loadContractors]);

  const countLabel = suspendedFilter === 'all'
    ? 'contractors registered'
    : suspendedFilter === 'suspended'
      ? 'suspended contractors'
      : suspendedFilter === 'deactivated'
        ? 'deactivated contractors'
        : 'active contractors';

  return (
    <>
      <div className="subpage-wrap subpage-wrap--admin subpage-wrap--contractors">
        <div className="subpage-header">
          <h1 className="subpage-title">Contractors</h1>
        </div>

        <div className="subpage-contractors-summary">
          Total:
          {' '}
          <strong>{total.toLocaleString('en-US')}</strong>
          {' '}
          {countLabel}
        </div>

        <AdminContractorSearch onSearch={handleSearch} showRange />

        <AdminContractorFilters
          certificates={certificates}
          selectedCertificates={selectedCertificates}
          onCertificateChange={handleCertificateChange}
          suspendedFilter={suspendedFilter}
          onSuspendedFilterChange={handleSuspendedFilterChange}
          registeredDateRange={registeredDateRange}
          onRegisteredDateRangeChange={handleRegisteredDateRangeChange}
          lastLoginRange={lastLoginRange}
          onLastLoginRangeChange={handleLastLoginRangeChange}
        />

        <div className="subpage-table-scroll subpage-table-scroll--contractors">
          <DataTable
            columns={columns}
            data={contractors}
            pagination
            paginationServer
            paginationComponent={RdtPagination}
            paginationRowsPerPageOptions={PER_PAGE_OPTIONS}
            paginationTotalRows={total}
            paginationPerPage={perPage}
            paginationDefaultPage={page}
            progressPending={loading}
            className="subpage-datatable subpage-datatable--contractors"
            progressComponent={(
              <div className="subpage-loading">
                <Loader color="white" />
                <span>Loading contractors…</span>
              </div>
            )}
            onChangePage={(nextPage) => setPage(nextPage)}
            onChangeRowsPerPage={(nextPerPage) => {
              setPage(1);
              setPerPage(nextPerPage);
            }}
            onSort={(column, direction) => {
              setPage(1);
              setSortBy(column.sortField || 'name');
              setSortDirection(direction === 'asc' ? 1 : -1);
            }}
            theme="sourcew9"
          />
        </div>
      </div>
      <Modal mainContainer={mainContainer.current} />
    </>
  );
};

const mapDispatchToProps = {
  toggleModal,
  reloadData,
};

const mapStateToProps = (state) => ({
  isReload: state.applicationState.isReload,
});

export default connect(mapStateToProps, mapDispatchToProps)(Contractors);
